import { useState } from 'react';
import { Button } from '@rudi/ui/button';
import { ChatModelFields, effortLabel } from '../components/ChatModelFields';
import { PageHeader } from '../components/page';
import { CHAT_BACKEND_LABELS } from '../api/chat-defaults';
import {
  AUTOMATION_EXECUTORS,
  automationModelsError,
  useAutomationModels,
  useUpdateAutomationModels,
  type AutomationExecutor,
  type AutomationModel,
  type AutomationModelRow,
  type AutomationModels,
} from '../api/automation-models';
import type { ChatModelOption } from '../api/types';

type Draft = { revision: number; automations: Record<string, AutomationModel | null> };

function summary(choice: AutomationModel, options: ChatModelOption[]): string {
  const label = options.find(option => option.model === choice.model)?.label ?? choice.model;
  return `${CHAT_BACKEND_LABELS[choice.backend]} · ${label} · ${effortLabel(choice.effort)}`;
}

function offered(choice: AutomationModel, data: AutomationModels): boolean {
  return data.model_options[choice.backend].some(
    option => option.model === choice.model && option.efforts.includes(choice.effort)
  );
}

/** The choice after switching executor: keep the model if the executor offers it. */
function onExecutor(
  choice: AutomationModel,
  backend: AutomationExecutor,
  data: AutomationModels
): AutomationModel {
  const options = data.model_options[backend].filter(option => option.efforts.length > 0);
  const same = options.find(option => option.model === choice.model);
  if (same)
    return {
      backend,
      model: same.model,
      effort: same.efforts.includes(choice.effort) ? choice.effort : same.efforts[0],
    };
  if (data.default.backend === backend) return { ...data.default };
  const first = options[0];
  if (!first) return { ...choice, backend };
  const effort = first.efforts.includes('medium') ? 'medium' : first.efforts[0];
  return { backend, model: first.model, effort };
}

function AutomationRow({
  row,
  choice,
  usesDefault,
  data,
  disabled,
  onChange,
}: {
  row: AutomationModelRow;
  choice: AutomationModel;
  usesDefault: boolean;
  data: AutomationModels;
  disabled: boolean;
  onChange: (choice: AutomationModel | null) => void;
}) {
  return (
    <fieldset aria-label={row.label} className="space-y-3 rounded-3xl border p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <legend className="text-sm font-medium">{row.label}</legend>
          <p className="mt-1 text-sm text-muted-foreground">{row.description}</p>
        </div>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
          {usesDefault ? 'Default' : 'Set here'}
        </span>
      </div>
      <label className="block space-y-1 text-sm">
        <span>Executor</span>
        <select
          aria-label={`${row.label} executor`}
          value={choice.backend}
          disabled={disabled}
          className="h-9 w-full rounded-3xl border border-input bg-background px-3"
          onChange={event =>
            onChange(onExecutor(choice, event.target.value as AutomationExecutor, data))
          }
        >
          {AUTOMATION_EXECUTORS.map(backend => (
            <option key={backend} value={backend}>
              {CHAT_BACKEND_LABELS[backend]}
            </option>
          ))}
        </select>
      </label>
      <ChatModelFields
        label={row.label}
        model={choice.model}
        effort={choice.effort}
        options={data.model_options[choice.backend]}
        disabled={disabled}
        onChange={(model, effort) => onChange({ backend: choice.backend, model, effort })}
      />
      {!usesDefault && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onChange(null)}
        >
          Use default
        </Button>
      )}
    </fieldset>
  );
}

export function AutomationModelsForm({ data }: { data: AutomationModels }) {
  const mutation = useUpdateAutomationModels();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const edits = draft?.automations ?? {};
  const conflict = !!draft && draft.revision !== data.revision;
  const changes = Object.entries(edits).filter(([key, choice]) => {
    const row = data.automations.find(item => item.key === key);
    if (!row) return false;
    if (choice === null) return row.source === 'platform';
    return (
      row.source === 'default' ||
      row.backend !== choice.backend ||
      row.model !== choice.model ||
      row.effort !== choice.effort
    );
  });
  const valid = changes.every(([, choice]) => choice === null || offered(choice, data));

  const edit = (key: string, choice: AutomationModel | null) => {
    setDraft({
      revision: draft?.revision ?? data.revision,
      automations: { ...edits, [key]: choice },
    });
    setError(null);
    setSaved(false);
  };
  const save = async () => {
    if (!changes.length || !valid || conflict || mutation.isPending) return;
    setError(null);
    setSaved(false);
    try {
      await mutation.mutateAsync({
        schema_version: 1,
        expected_revision: data.revision,
        automations: Object.fromEntries(changes),
      });
      setDraft(null);
      setSaved(true);
    } catch (error) {
      setError(automationModelsError(error));
    }
  };

  return (
    <form
      aria-label="Automation models"
      className="max-w-2xl space-y-4"
      onSubmit={event => {
        event.preventDefault();
        void save();
      }}
    >
      <p className="text-sm text-muted-foreground">
        Automations without their own choice run on the default:{' '}
        {summary(data.default, data.model_options[data.default.backend])}.
      </p>
      {data.automations.map(row => {
        const edited = edits[row.key];
        const usesDefault = edited === null || (edited === undefined && row.source === 'default');
        const choice = edited ?? (edited === null ? data.default : row);
        return (
          <AutomationRow
            key={row.key}
            row={row}
            choice={{ backend: choice.backend, model: choice.model, effort: choice.effort }}
            usesDefault={usesDefault}
            data={data}
            disabled={mutation.isPending}
            onChange={next => edit(row.key, next)}
          />
        );
      })}
      <p className="text-sm text-muted-foreground">
        The Codex CLI runs on the environment's Codex runner. OpenAI models use each tenant's OpenAI
        API key; OpenRouter models use Rudi's OpenRouter key, or an API key named openrouter, and
        are sent without data retention.
      </p>
      {conflict && (
        <p role="alert" className="text-sm text-destructive">
          The automation models changed while you were editing. Discard your edits to load the
          latest choices.
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {saved && (
        <p role="status" className="text-sm">
          Automation models saved.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          type="submit"
          disabled={mutation.isPending || !changes.length || !valid || conflict}
        >
          {mutation.isPending ? 'Saving…' : 'Save automation models'}
        </Button>
        {draft && (
          <Button
            type="button"
            variant="outline"
            disabled={mutation.isPending}
            onClick={() => {
              setDraft(null);
              setError(null);
              setSaved(false);
            }}
          >
            Discard edits
          </Button>
        )}
      </div>
    </form>
  );
}

export function AutomationModelsPage() {
  const models = useAutomationModels();
  return (
    <div className="space-y-4">
      <PageHeader
        title="Automation models"
        description="Choose the model each AI automation of the connected Rudi runs on."
      />
      {models.error && (
        <div role="alert" className="space-y-2 text-sm text-destructive">
          <p>{automationModelsError(models.error)}</p>
          <Button variant="outline" onClick={() => void models.refetch()}>
            Retry
          </Button>
        </div>
      )}
      {models.data ? (
        <AutomationModelsForm data={models.data} />
      ) : models.isLoading ? (
        <p>Loading automation models…</p>
      ) : models.error ? null : (
        <section className="space-y-2 rounded-3xl border p-4">
          <h2 className="text-sm font-medium">Automation models unavailable</h2>
          <p className="text-sm text-muted-foreground">
            This Rudi connection does not support choosing automation models yet. It needs a newer
            Rudi release.
          </p>
          <Button variant="outline" onClick={() => void models.refetch()}>
            Check again
          </Button>
        </section>
      )}
    </div>
  );
}
